'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import type { DirectoryRow } from '@/lib/directory/row'
import { tw } from '@/lib/utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { RowClassification } from './row-classification'
import { RowEvidence } from './row-evidence'
import { RowFoot } from './row-foot'
import { RowIdentity } from './row-identity'
import { RowImage } from './row-image'
import { RowSoundScore } from './row-sound-score'
import { RowSpecs } from './row-specs'

interface ReleaseRowProps {
  release: DirectoryRow
  eagerImage: boolean
  isCompared: boolean
  atMax: boolean
  facetLabels: Map<string, string>
  onToggleCompare: (slug: string) => void
}

// Keep the name, classification, and features beside the image at every
// width. SoundScore belongs to the desktop evidence column; the mobile
// footer follows the features directly.
export const ReleaseRow = ({
  release,
  eagerImage,
  isCompared,
  atMax,
  facetLabels,
  onToggleCompare,
}: ReleaseRowProps) => {
  const { locale } = useTranslation()
  const href = localizedHref(locale, release.path)
  const headingId = `directory-release-${release.slug}`
  const card = release.products.find((product) => product.slug === release.cardSlug) ?? release.products[0]
  const hasScore = release.score != null && release.score > 0
  // With no rating at all the "Not yet tested" chip says it once; a meter
  // reading "Not tested" beneath would only repeat it.
  const showSoundScore = hasScore || (release.soundScore != null && release.soundScore > 0)

  return (
    <li>
      <article
        data-directory-row
        data-selected={isCompared}
        aria-labelledby={headingId}
        className={tw(
          'grid grid-cols-[104px_minmax(0,1fr)_auto] grid-rows-[auto_1fr_auto] gap-x-3 gap-y-3 rounded-2xl border bg-white p-4 transition-colors xs:grid-cols-[112px_minmax(0,1fr)_auto] md:grid-cols-[128px_minmax(0,1fr)_auto] md:gap-x-6 md:p-5 laptop:grid-cols-[160px_minmax(0,1fr)_224px] laptop:gap-y-2 desktop:grid-cols-[192px_minmax(0,1fr)_224px]',
          isCompared ? 'border-navy ring-navy ring-1' : 'border-navy/10 hover:border-navy/30'
        )}
      >
        <RowImage image={release.image} productName={release.fullName} href={href} eager={eagerImage} />
        <RowIdentity id={headingId} brandName={release.brandName} fullName={release.fullName} href={href}>
          <RowClassification product={card} facetLabels={facetLabels} />
        </RowIdentity>
        <div className="col-start-3 row-start-1 self-start justify-self-end laptop:row-[1/3] laptop:w-full laptop:justify-self-start">
          <RowEvidence score={release.score} />
          {showSoundScore ? (
            <RowSoundScore
              score={release.soundScore}
              className="border-navy/10 mt-2 hidden border-t pt-3 laptop:block"
            />
          ) : null}
        </div>
        <div className="col-[2/4] row-start-2 min-w-0 laptop:col-[2/3] laptop:self-end laptop:pb-2">
          <RowSpecs specs={card?.specs} />
        </div>
        <RowFoot
          release={release}
          href={href}
          isCompared={isCompared}
          atMax={atMax}
          onToggleCompare={onToggleCompare}
        />
      </article>
    </li>
  )
}
